import { IFilterAngularComp } from 'ag-grid-angular';
import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RowNode } from 'ag-grid-community';

import { FormsModule } from '@angular/forms';
import { StateLevelsService } from '../../../@core/utils';

@Component({
    selector:'app-last-technical-state-filter',
    template: `
      <div class="technical-state-filter-div">
        <label class="technical-state-filter-label">
          <input
            class="technical-state-filter-input"
            type="checkbox"
            [ngModel]="levelsSelected && levelsSelected.length === filteredLevelsMap?.length"
            (change)="selectAll()"
            >
            <span class="technical-state-filter-checkmark"></span>
            &nbsp;{{ selectAllLabel }}
          </label>
          @for (level of filteredLevelsMap; track level) {
            <label class="technical-state-filter-label">
              <input
                class="technical-state-filter-input"
                type="checkbox"
                name="level.id"
                value="level.id"
                id="level.id"
                [ngModel]="levelsSelected && levelsSelected.includes(level.id)"
                (change)="onChange(level.id)"
                >
                <span class="technical-state-filter-checkmark"></span>
                &nbsp;{{ level.label }}
              </label>
            }
          </div>
      `,
    standalone: true,
    imports: [FormsModule],
})
export class LastTechnicalStateFilterComponent implements IFilterAngularComp {

    constructor(
        private translate: TranslateService,
        private stateLevelsService: StateLevelsService,
    ) { }

    public params: any;
    public valueGetter: (rowNode: RowNode) => any;
    public levelsSelected: number[] = [null, 0, 1, 2, 3];
    public levelsMap: any[];
    public filteredLevelsMap: any[];
    public selectAllLabel: string;
    private levelByNotesMap = {
        null: { noteFrom: null, noteTo: null }, // No notation
        0: { noteFrom: 0, noteTo: 15 }, // Critical
        1: { noteFrom: 16, noteTo: 35 }, // Bad
        2: { noteFrom: 36, noteTo: 50 }, // Acceptable
        3: { noteFrom: 51, noteTo: 100 }, // Good
    };
    public greaterThanOrEqual: any;
    public lessThanOrEqual: any;

    agInit(params: any): void {
        this.params = params;
        this.levelsMap = this.params.context.levelsMap;
        this.valueGetter = params.valueGetter;
        if (!this.translate && this.params.context?.translate) {
            this.translate = this.params.context.translate;
        }
        this.selectAllLabel = this.translate.instant('pages.common.ag-grid.internal.select_all');
        this.updateFilteredLevelsMap();
    }

    private convertRawToNotation(rawValue: number | null): number | null {
        if (rawValue === null || rawValue === undefined) {
            return null;
        }
        const mapped = this.stateLevelsService.getTechnicalStateNotationFromLevel(rawValue);
        return mapped != null ? mapped : null;
    }

    private updateFilteredLevelsMap(): void {
        const existingLevels = new Set<number | null>();

        this.params.api.forEachNode((node: RowNode) => {
            const field = this.params.colDef.field;
            if (node.data && node.data[field] !== undefined) {
                const notation = this.convertRawToNotation(node.data[field]);
                const level = this.getNotationLevel(notation);
                existingLevels.add(level);
            }
        });

        this.filteredLevelsMap = this.levelsMap.filter(level => existingLevels.has(level.id));
    }

    private getNotationLevel(notation: number | null): number | null {
        if (notation === null || notation === undefined) {
            return null;
        }

        for (const [level, range] of Object.entries(this.levelByNotesMap)) {
            const levelNum = level === 'null' ? null : Number(level);
            if (levelNum === null && notation === null) {
                return null;
            }
            if (levelNum !== null && range.noteFrom !== null && range.noteTo !== null && range.noteFrom <= notation && range.noteTo >= notation) {
                return levelNum;
            }
        }

        return null;
    }

    isFilterActive(): boolean {
        return this.levelsSelected?.length !== this.filteredLevelsMap?.length;
    }

    doesFilterPass(params): boolean {
        if (!params.data) {
            return false;
        }
        const field = this.params.colDef.field;
        const notation = this.convertRawToNotation(params.data[field]);
        if (notation === null || notation === undefined) {
            return this.levelsSelected.includes(null);
        }
        return this.levelsSelected.some(level => {
            if (level !== null) {
                return this.levelByNotesMap[level].noteFrom <= notation
                    && this.levelByNotesMap[level].noteTo >= notation;
            }
            return false;
        });
    }

    getModel(): any {
        return { values: this.levelsSelected, filterType: 'set' };
    }

    setModel(model: any): void {
        this.updateFilteredLevelsMap();

        if (model?.values) {
            this.levelsSelected = model.values.map((value) => value !== null ? Number(value) : value);
        } else {
            this.levelsSelected = this.filteredLevelsMap?.map(level => level.id) || [null, 0, 1, 2, 3];
        }
    }

    onFilterChanged(): void {
        this.updateFilteredLevelsMap();
    }

    onChange(newValue: any): void {
        const levelIsSelected = this.levelsSelected.findIndex(level => level === newValue);
        if (levelIsSelected > -1) {
            this.levelsSelected.splice(levelIsSelected, 1);
        } else {
            this.levelsSelected.push(newValue);
        }
        this.params.filterChangedCallback();
    }

    selectAll(): void {
        if (this.levelsSelected?.length !== this.filteredLevelsMap?.length) {
            this.levelsSelected = this.filteredLevelsMap.map(level => level.id);
        } else {
            this.levelsSelected = [];
        }

        this.params.filterChangedCallback();
    }
}
